/**
 * FFT2D.ts — 2D radix-2 FFT/IFFT helper for N×N rg32float (complex) textures.
 *
 * Uses the shared `shaders/fft.compute.wgsl` row/column kernel. A 2D FFT is a
 * row pass followed by a column pass; both passes share the same pipeline.
 *
 * The caller provides:
 *   - `inputView`        : sampled view of the input texture (any rg32float view).
 *   - `scratchTex`       : N×N rg32float storage texture used as ping-pong.
 *   - `outputView`       : storage view to write the final result.
 *
 * One scratch texture is enough because passes are: src → scratch → dst.
 *
 * N is hard-coded to 256 in the shader (matches stage-1 cascade requirement).
 */

import fftWgsl from "../../shaders/fft.compute.wgsl?raw";
import { createUniformBuffer } from "../../gpu/Buffers";

export const FFT_N = 256;

export interface FFT2DResources {
  /** Texture sampled as input. Same N×N rg32float. */
  inputView: GPUTextureView;
  /** Storage view of the input texture (write target between passes). */
  scratchView: GPUTextureView;
  /** Sampled view of the scratch texture (read for the column pass). */
  scratchSampledView: GPUTextureView;
  /** Storage view receiving the final 2D-FFT result. */
  outputView: GPUTextureView;
}

export class FFT2D {
  private pipeline: GPUComputePipeline;
  private bgl: GPUBindGroupLayout;
  private rowParams: GPUBuffer;
  private colParams: GPUBuffer;

  /**
   * @param inverse  When true, compute IFFT (with 1/N² normalization across both passes).
   */
  constructor(
    private device: GPUDevice,
    inverse: boolean,
  ) {
    const module = device.createShaderModule({
      label: "fft.compute.wgsl",
      code: fftWgsl,
    });
    this.bgl = device.createBindGroupLayout({
      label: "FFT2D.bgl",
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.COMPUTE,
          buffer: { type: "uniform" },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.COMPUTE,
          texture: { sampleType: "unfilterable-float" },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.COMPUTE,
          storageTexture: { access: "write-only", format: "rg32float" },
        },
      ],
    });
    this.pipeline = device.createComputePipeline({
      label: "FFT2D.pipeline",
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bgl] }),
      compute: { module, entryPoint: "main" },
    });

    // Two small uniform buffers, one per pass direction. Layout: rowPass, inverse, _pad0, _pad1.
    const inv = inverse ? 1 : 0;
    this.rowParams = createUniformBuffer(device, 16, "FFT2D.rowParams");
    this.colParams = createUniformBuffer(device, 16, "FFT2D.colParams");
    device.queue.writeBuffer(
      this.rowParams,
      0,
      new Uint32Array([1, inv, 0, 0]),
    );
    device.queue.writeBuffer(
      this.colParams,
      0,
      new Uint32Array([0, inv, 0, 0]),
    );
  }

  /** Encode the row + column passes (separate compute passes for synchronization). */
  encode(encoder: GPUCommandEncoder, res: FFT2DResources): void {
    const rowBg = this.device.createBindGroup({
      label: "FFT2D.rowBg",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.rowParams } },
        { binding: 1, resource: res.inputView },
        { binding: 2, resource: res.scratchView },
      ],
    });
    const colBg = this.device.createBindGroup({
      label: "FFT2D.colBg",
      layout: this.bgl,
      entries: [
        { binding: 0, resource: { buffer: this.colParams } },
        { binding: 1, resource: res.scratchSampledView },
        { binding: 2, resource: res.outputView },
      ],
    });

    {
      const pass = encoder.beginComputePass({ label: "FFT2D.rowPass" });
      pass.setPipeline(this.pipeline);
      pass.setBindGroup(0, rowBg);
      pass.dispatchWorkgroups(FFT_N, 1, 1);
      pass.end();
    }
    {
      const pass = encoder.beginComputePass({ label: "FFT2D.colPass" });
      pass.setPipeline(this.pipeline);
      pass.setBindGroup(0, colBg);
      pass.dispatchWorkgroups(FFT_N, 1, 1);
      pass.end();
    }
  }

  destroy(): void {
    this.rowParams.destroy();
    this.colParams.destroy();
  }
}
